<template>
  <div class="track-page-layout event-page-layout" :class="{ 'track-page-layout--no-map': !showMap }">
    <div v-if="isMobile" class="track-mobile-header">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <div
        v-if="event || loading"
        class="dashboard-tab-bar"
        role="tablist"
        aria-label="Разделы события"
      >
        <button
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': mobileTab === 'description' }"
          role="tab"
          :aria-selected="mobileTab === 'description'"
          @click="mobileTab = 'description'"
        >
          Описание
        </button>
        <button
          v-if="showMap"
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': mobileTab === 'map' }"
          role="tab"
          :aria-selected="mobileTab === 'map'"
          @click="mobileTab = 'map'"
        >
          Карта
        </button>
      </div>
    </div>

    <aside
      v-show="!isMobile || mobileTab === 'description'"
      class="track-sidebar"
      :class="{ 'track-sidebar--mobile-tab': isMobile }"
    >
      <div class="track-sidebar-scroll">
        <div v-if="loading" class="track-sidebar-content">
          <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />
          <h1 class="track-title">{{ event?.name || 'Событие' }}</h1>
          <p class="empty-state">Загрузка...</p>
        </div>

        <div v-else-if="error" class="track-sidebar-content">
          <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />
          <h1 class="track-title">{{ event?.name || 'Событие' }}</h1>
          <p class="error-message">{{ error }}</p>
        </div>

        <div v-else-if="event" class="track-sidebar-content">
          <AppBreadcrumbs v-if="!isMobile" :items="breadcrumbItems" />

          <div v-if="event.cover_photo_url" class="event-cover">
            <img :src="event.cover_photo_thumb_url || event.cover_photo_url" :alt="event.name" />
          </div>

          <h1 class="track-title">{{ event.name }}</h1>
          <p class="track-item-meta">{{ formatEventDateRange(event.starts_at, event.ends_at) }}</p>
          <p v-if="!event.tracks?.length && event.venue" class="track-item-meta event-venue">
            Место проведения: {{ event.venue }}
          </p>
          <p class="track-item-meta">{{ visibilityLabel(event.public) }}</p>

          <fieldset v-if="isOwner" class="visibility-field">
            <legend class="track-item-meta">Видимость</legend>
            <label class="radio-label">
              <input
                type="radio"
                :checked="!event.public"
                :disabled="updatingVisibilitySettings"
                @change="toggleVisibility(false)"
              />
              Приватное
            </label>
            <label class="radio-label">
              <input
                type="radio"
                :checked="event.public"
                :disabled="updatingVisibilitySettings"
                @change="toggleVisibility(true)"
              />
              Публичное
            </label>
          </fieldset>

          <div v-if="event.organizers?.length" class="event-organizers">
            <h3>{{ event.organizers.length > 1 ? 'Организаторы' : 'Организатор' }}</h3>
            <ul class="organizer-list">
              <li
                v-for="(organizer, index) in event.organizers"
                :key="organizerKey(organizer, index)"
                class="organizer-list-item"
              >
                <template v-if="organizer.kind === 'user'">
                  <div
                    v-if="organizer.user.id === event.user_id && !isOwner && auth.isAuthenticated"
                    class="author-row"
                  >
                    <AuthorLink :author="organizer.user" size="sm" class="track-item-meta" />
                    <FavoriteButton
                      label="Автор в избранное"
                      :favorited="authorFavorited"
                      :disabled="favoritingAuthor"
                      @favorite="favoriteAuthor"
                      @unfavorite="unfavoriteAuthor"
                    />
                  </div>
                  <AuthorLink
                    v-else
                    :author="organizer.user"
                    size="sm"
                    class="track-item-meta track-item-author"
                  />
                </template>
                <div v-else class="external-organizer track-item-meta">
                  <span class="external-organizer-name">{{ organizer.name }}</span>
                  <a
                    v-if="contactHref(organizer.contact)"
                    :href="contactHref(organizer.contact)"
                    class="external-organizer-contact"
                  >
                    {{ organizer.contact }}
                  </a>
                  <span v-else class="external-organizer-contact">{{ organizer.contact }}</span>
                </div>
              </li>
            </ul>
          </div>

          <p v-if="isPlainDescription" class="event-description">{{ event.description }}</p>
          <div
            v-else-if="sanitizedDescription"
            class="event-description event-description--rich"
            v-html="sanitizedDescription"
          />

          <div v-if="event.participants?.length" class="event-participants">
            <h3>Участники ({{ event.participants_count ?? event.participants.length }})</h3>
            <div class="participant-chips">
              <AuthorLink
                v-for="participant in event.participants"
                :key="participant.id"
                :author="participant"
                size="sm"
              />
            </div>
          </div>

          <div v-if="event.tracks?.length" class="linked-tracks">
            <h3>Треки</h3>
            <ul class="linked-tracks-list">
              <li v-for="(track, index) in event.tracks" :key="track.id" class="linked-track-item">
                <span class="track-color-dot" :style="{ backgroundColor: trackMapColor(index) }" />
                <div class="linked-track-info">
                  <router-link :to="{ name: 'track', params: { id: track.id } }">
                    {{ track.name }}
                  </router-link>
                  <p v-if="track.distance_m != null" class="track-item-meta">
                    {{ formatDistance(track.distance_m) }}
                    <template v-if="track.duration_s != null">
                      · {{ formatDuration(track.duration_s) }}
                    </template>
                    <template v-if="track.coordinates?.length"> · старт / финиш</template>
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div v-if="auth.isAuthenticated && !isOwner" class="event-membership btn-row">
            <button
              class="btn"
              :class="event.subscribed ? 'btn-secondary' : ''"
              :disabled="subscribing"
              @click="toggleSubscription"
            >
              {{ event.subscribed ? 'Отписаться' : 'Подписаться' }}
            </button>
            <button
              class="btn"
              :class="event.participating ? 'btn-secondary' : ''"
              :disabled="participating"
              @click="toggleParticipation"
            >
              {{ event.participating ? 'Выйти' : 'Участвовать' }}
            </button>
          </div>

          <div v-if="auth.isAuthenticated" class="track-actions">
            <ReactionBar
              :likes-count="event.likes_count ?? 0"
              :dislikes-count="event.dislikes_count ?? 0"
              :user-reaction="event.user_reaction"
              :disabled="reactingEvent"
              @react="reactToEvent"
              @unreact="unreactToEvent"
            />
            <span class="track-stats">{{ event.comments_count ?? 0 }} 💬</span>
            <FavoriteButton
              label="Событие в избранное"
              :favorited="event.favorited"
              :disabled="favoritingEvent"
              @favorite="favoriteCurrentEvent"
              @unfavorite="unfavoriteCurrentEvent"
            />
          </div>

          <div v-else class="track-actions track-actions--readonly">
            <span>{{ event.likes_count ?? 0 }} 👍</span>
            <span>{{ event.dislikes_count ?? 0 }} 👎</span>
            <span>{{ event.comments_count ?? 0 }} 💬</span>
          </div>

          <GuestAuthPrompt v-if="!auth.isAuthenticated" />

          <div v-if="isOwner" class="btn-row" style="margin-top: 1rem">
            <router-link class="btn btn-secondary" :to="{ name: 'event-edit', params: { id: event.id } }">
              Редактировать
            </router-link>
            <button class="btn btn-danger" :disabled="deleting" @click="removeEvent">
              {{ deleting ? 'Удаление...' : 'Удалить' }}
            </button>
          </div>

          <section class="comments-section panel">
            <h2>Комментарии</h2>

            <label v-if="isOwner" class="comments-setting">
              <input
                type="checkbox"
                :checked="event.allow_comments"
                :disabled="updatingCommentsSettings"
                @change="toggleAllowComments"
              />
              Разрешить комментарии
            </label>

            <CommentList
              :comments="comments"
              :can-delete="canDeleteComment"
              :interactive="auth.isAuthenticated"
              :deleting-id="deletingCommentId"
              :reacting-id="reactingCommentId"
              @delete="removeComment"
              @react="reactToComment"
              @unreact="unreactToComment"
            />

            <LoadMoreButton
              :has-more="hasMorePages(commentsPagination)"
              :loading="commentsLoadingMore"
              :disabled="commentsLoading"
              @click="loadMoreComments"
            />

            <CommentForm
              v-if="canComment"
              text-only
              :submitting="submittingComment"
              :error="commentError"
              @submit="submitComment"
            />
          </section>
        </div>
      </div>
    </aside>

    <div
      v-if="showMap"
      v-show="!isMobile || mobileTab === 'map'"
      class="track-map-wrap"
      :class="{ 'track-map-wrap--mobile-tab': isMobile }"
    >
      <TrackMap :tracks="mapTracks" :picked-point="venuePoint" show-endpoints />
    </div>
  </div>
</template>

<script setup>
import DOMPurify from 'dompurify'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import AuthorLink from '../components/AuthorLink.vue'
import CommentForm from '../components/CommentForm.vue'
import CommentList from '../components/CommentList.vue'
import LoadMoreButton from '../components/LoadMoreButton.vue'
import FavoriteButton from '../components/FavoriteButton.vue'
import GuestAuthPrompt from '../components/GuestAuthPrompt.vue'
import ReactionBar from '../components/ReactionBar.vue'
import TrackMap from '../components/TrackMap.vue'
import { useMediaQuery } from '../composables/useMediaQuery'
import { favoriteUser, fetchFavoriteUsers, unfavoriteUser } from '../api/favorites'
import {
  createEventComment,
  deleteEvent,
  deleteEventComment,
  favoriteEvent,
  fetchEvent,
  fetchEventComments,
  joinEvent,
  leaveEvent,
  removeEventCommentReaction,
  removeEventReaction,
  setEventCommentReaction,
  setEventReaction,
  subscribeToEvent,
  unfavoriteEvent,
  unsubscribeFromEvent,
  updateEventCommentsSettings,
  updateEventVisibilitySettings,
} from '../api/events'
import { useAuthStore } from '../stores/auth'
import { formatEventDateRange, visibilityLabel } from '../utils/eventFormat'
import {
  EVENT_DESCRIPTION_ALLOWED_ATTR,
  EVENT_DESCRIPTION_ALLOWED_TAGS,
  isHtmlContent,
} from '../utils/richDescription'
import { trackMapColor } from '../utils/trackMapColors'
import { formatDistance, formatDuration } from '../utils/trackFormat'
import { userDisplayName } from '../utils/userDisplay'
import { contactHref } from '../utils/organizerDisplay'
import { hasMorePages, parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const isMobile = useMediaQuery('(max-width: 767px)')
const mobileTab = ref('description')

const event = ref(null)
const comments = ref([])
const commentsPage = ref(1)
const commentsPagination = ref(null)
const commentsLoading = ref(false)
const commentsLoadingMore = ref(false)
const loading = ref(true)
const error = ref('')
const deleting = ref(false)
const submittingComment = ref(false)
const commentError = ref('')
const deletingCommentId = ref(null)
const reactingCommentId = ref(null)
const reactingEvent = ref(false)
const favoritingEvent = ref(false)
const favoritingAuthor = ref(false)
const authorFavorited = ref(false)
const updatingCommentsSettings = ref(false)
const updatingVisibilitySettings = ref(false)
const subscribing = ref(false)
const participating = ref(false)

const breadcrumbItems = computed(() => [
  { label: 'События', to: { name: 'events-home' } },
  { label: event.value?.name || 'Событие' },
])

const isOwner = computed(() => {
  if (!event.value || !auth.user) return false
  return event.value.user_id === auth.user.id
})

const canComment = computed(() => {
  if (!event.value || !auth.user) return false
  return isOwner.value || event.value.allow_comments
})

const isPlainDescription = computed(() => {
  return event.value?.description && !isHtmlContent(event.value.description)
})

const sanitizedDescription = computed(() => {
  if (!event.value?.description || isPlainDescription.value) return ''

  return DOMPurify.sanitize(event.value.description, {
    ALLOWED_TAGS: EVENT_DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: EVENT_DESCRIPTION_ALLOWED_ATTR,
  })
})

const mapTracks = computed(() => {
  if (!event.value?.tracks?.length) return []

  return event.value.tracks
    .filter((track) => track.coordinates?.length)
    .map((track, index) => ({
      id: track.id,
      name: track.name,
      coordinates: track.coordinates,
      distance_m: track.distance_m,
      duration_s: track.duration_s,
      color: trackMapColor(index),
    }))
})

const venuePoint = computed(() => {
  if (mapTracks.value.length > 0) return null
  if (event.value?.venue_lat == null || event.value?.venue_lng == null) return null

  return { lat: event.value.venue_lat, lng: event.value.venue_lng }
})

const showMap = computed(() => mapTracks.value.length > 0 || Boolean(venuePoint.value))

function organizerKey(organizer, index) {
  if (organizer.kind === 'user') return `user-${organizer.user.id}`
  return organizer.id ? `external-${organizer.id}` : `external-${index}`
}

function canDeleteComment(comment) {
  if (!event.value || !auth.user) return false
  return comment.author.id === auth.user.id || isOwner.value
}

async function loadEvent() {
  loading.value = true
  error.value = ''
  commentsPage.value = 1

  try {
    const { data } = await fetchEvent(route.params.id)
    event.value = data
    await Promise.all([loadComments(), loadAuthorFavoriteState()])
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось загрузить событие'
  } finally {
    loading.value = false
  }
}

async function loadAuthorFavoriteState() {
  if (!auth.isAuthenticated || !event.value || isOwner.value) {
    authorFavorited.value = false
    return
  }

  const { data } = await fetchFavoriteUsers()
  authorFavorited.value = data.some((author) => author.id === event.value.user_id)
}

async function loadComments({ reset = true } = {}) {
  if (!event.value) return

  if (reset) {
    commentsLoading.value = true
  } else {
    commentsLoadingMore.value = true
  }
  try {
    const response = await fetchEventComments(event.value.id, {
      page: commentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    comments.value = reset ? items : [...comments.value, ...items]
    commentsPagination.value = pagination
  } finally {
    commentsLoading.value = false
    commentsLoadingMore.value = false
  }
}

function loadMoreComments() {
  commentsPage.value += 1
  loadComments({ reset: false })
}

function updateCommentsPaginationTotal(total) {
  if (!commentsPagination.value) return

  const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE)
  commentsPagination.value = {
    ...commentsPagination.value,
    total,
    total_pages: totalPages,
  }
}

async function removeEvent() {
  if (!event.value) return

  deleting.value = true
  try {
    await deleteEvent(event.value.id)
    router.push({ name: 'events-home' })
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить событие'
  } finally {
    deleting.value = false
  }
}

async function submitComment(formData, { reset }) {
  if (!event.value) return

  submittingComment.value = true
  commentError.value = ''

  try {
    const { data } = await createEventComment(event.value.id, formData)
    const newCount = (event.value.comments_count ?? 0) + 1
    event.value = {
      ...event.value,
      comments_count: newCount,
    }
    const lastPage = Math.max(1, Math.ceil(newCount / PAGE_SIZE))

    if (commentsPage.value >= lastPage) {
      comments.value = [...comments.value, data]
      updateCommentsPaginationTotal(newCount)
    } else {
      commentsPage.value = lastPage
      await loadComments({ reset: true })
    }
    reset()
  } catch (err) {
    const errors = err.response?.data?.errors
    commentError.value =
      errors?.join(', ') || err.response?.data?.error || 'Не удалось добавить комментарий'
  } finally {
    submittingComment.value = false
  }
}

async function removeComment(comment) {
  if (!event.value) return

  deletingCommentId.value = comment.id
  try {
    await deleteEventComment(event.value.id, comment.id)
    const newCount = Math.max((event.value.comments_count ?? 0) - 1, 0)
    event.value = {
      ...event.value,
      comments_count: newCount,
    }
    comments.value = comments.value.filter((item) => item.id !== comment.id)

    if (comments.value.length === 0 && commentsPage.value > 1) {
      commentsPage.value -= 1
      await loadComments({ reset: true })
    } else {
      updateCommentsPaginationTotal(newCount)
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось удалить комментарий'
  } finally {
    deletingCommentId.value = null
  }
}

async function toggleAllowComments(changeEvent) {
  if (!event.value) return

  updatingCommentsSettings.value = true
  try {
    const { data } = await updateEventCommentsSettings(event.value.id, changeEvent.target.checked)
    event.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить настройки'
  } finally {
    updatingCommentsSettings.value = false
  }
}

async function toggleVisibility(isPublic) {
  if (!event.value || event.value.public === isPublic) return

  updatingVisibilitySettings.value = true
  try {
    const { data } = await updateEventVisibilitySettings(event.value.id, isPublic)
    event.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось обновить видимость'
  } finally {
    updatingVisibilitySettings.value = false
  }
}

async function reactToEvent(value) {
  if (!event.value) return

  reactingEvent.value = true
  try {
    const { data } = await setEventReaction(event.value.id, value)
    event.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingEvent.value = false
  }
}

async function unreactToEvent() {
  if (!event.value) return

  reactingEvent.value = true
  try {
    await removeEventReaction(event.value.id)
    const { data } = await fetchEvent(event.value.id)
    event.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingEvent.value = false
  }
}

async function favoriteCurrentEvent() {
  if (!event.value) return

  favoritingEvent.value = true
  try {
    const { data } = await favoriteEvent(event.value.id)
    event.value = data
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить в избранное'
  } finally {
    favoritingEvent.value = false
  }
}

async function unfavoriteCurrentEvent() {
  if (!event.value) return

  favoritingEvent.value = true
  try {
    await unfavoriteEvent(event.value.id)
    event.value = { ...event.value, favorited: false }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать из избранного'
  } finally {
    favoritingEvent.value = false
  }
}

async function toggleSubscription() {
  if (!event.value) return

  subscribing.value = true
  try {
    if (event.value.subscribed) {
      await unsubscribeFromEvent(event.value.id)
      event.value = { ...event.value, subscribed: false }
    } else {
      const { data } = await subscribeToEvent(event.value.id)
      event.value = data
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось изменить подписку'
  } finally {
    subscribing.value = false
  }
}

async function toggleParticipation() {
  if (!event.value) return

  participating.value = true
  try {
    if (event.value.participating) {
      await leaveEvent(event.value.id)
      const { data } = await fetchEvent(event.value.id)
      event.value = data
    } else {
      const { data } = await joinEvent(event.value.id)
      event.value = data
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось изменить участие'
  } finally {
    participating.value = false
  }
}

async function favoriteAuthor() {
  if (!event.value) return

  favoritingAuthor.value = true
  try {
    await favoriteUser(event.value.user_id)
    authorFavorited.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось добавить автора в избранное'
  } finally {
    favoritingAuthor.value = false
  }
}

async function unfavoriteAuthor() {
  if (!event.value) return

  favoritingAuthor.value = true
  try {
    await unfavoriteUser(event.value.user_id)
    authorFavorited.value = false
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось убрать автора из избранного'
  } finally {
    favoritingAuthor.value = false
  }
}

async function reactToComment(comment, value) {
  if (!event.value) return

  reactingCommentId.value = comment.id
  try {
    const { data } = await setEventCommentReaction(event.value.id, comment.id, value)
    comments.value = comments.value.map((item) => (item.id === comment.id ? data : item))
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось поставить реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

async function unreactToComment(comment) {
  if (!event.value) return

  reactingCommentId.value = comment.id
  try {
    await removeEventCommentReaction(event.value.id, comment.id)
    comments.value = comments.value.map((item) =>
      item.id === comment.id
        ? {
            ...comment,
            user_reaction: null,
            likes_count:
              comment.user_reaction === 'like'
                ? Math.max((comment.likes_count ?? 0) - 1, 0)
                : comment.likes_count,
            dislikes_count:
              comment.user_reaction === 'dislike'
                ? Math.max((comment.dislikes_count ?? 0) - 1, 0)
                : comment.dislikes_count,
          }
        : item
    )
  } catch (err) {
    error.value = err.response?.data?.error || 'Не удалось снять реакцию'
  } finally {
    reactingCommentId.value = null
  }
}

onMounted(loadEvent)
watch(() => route.params.id, loadEvent)
onBeforeRouteUpdate(loadEvent)
watch(showMap, (visible) => {
  if (!visible && mobileTab.value === 'map') {
    mobileTab.value = 'description'
  }
})
</script>

<style scoped>
.track-page-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.track-page-layout--no-map .track-sidebar {
  max-height: none;
  flex: 1;
}

@media (min-width: 768px) {
  .track-page-layout--no-map.event-page-layout .track-sidebar {
    width: 100%;
    border-right: none;
  }
}

.track-mobile-header {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 1.25rem 0;
}

.track-mobile-header .dashboard-tab-bar {
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.track-sidebar {
  width: 100%;
  max-height: 45vh;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.track-sidebar--mobile-tab {
  flex: 1;
  max-height: none;
  min-height: 0;
  border-bottom: none;
}

.track-title {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
}

.track-sidebar-content {
  padding: 1rem 1.25rem;
}

.track-sidebar-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.track-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.track-stats {
  font-size: 0.875rem;
  color: #334155;
}

.visibility-field {
  border: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.visibility-field legend {
  margin-bottom: 0.375rem;
  padding: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.author-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.author-row .track-item-meta {
  margin-top: 0;
}

.comments-section {
  margin-top: 1.5rem;
}

.comments-section h2 {
  margin-top: 0;
}

.comments-setting {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.track-map-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.track-map-wrap--mobile-tab {
  flex: 1;
  min-height: 0;
}

.event-cover {
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;
}

.event-cover img {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: cover;
}

.event-description {
  white-space: pre-wrap;
  margin: 1rem 0 0;
}

.event-description--rich :deep(h2),
.event-description--rich :deep(h3) {
  margin: 1rem 0 0.5rem;
}

.event-participants,
.event-organizers,
.linked-tracks {
  margin-top: 1rem;
}

.event-participants h3,
.event-organizers h3,
.linked-tracks h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
}

.organizer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.organizer-list-item {
  margin: 0;
}

.external-organizer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.external-organizer-name {
  font-weight: 600;
}

.external-organizer-contact {
  color: #2563eb;
  text-decoration: none;
}

.external-organizer-contact:hover {
  text-decoration: underline;
}

.participant-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.linked-tracks-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.linked-track-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.track-color-dot {
  flex-shrink: 0;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  margin-top: 0.35rem;
}

.linked-track-info {
  min-width: 0;
}

.linked-track-info .track-item-meta {
  margin: 0.25rem 0 0;
}

.event-membership {
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .event-page-layout {
    flex-direction: row;
    height: 100%;
  }

  .event-page-layout .track-sidebar {
    flex: 1;
    width: 50%;
    max-width: none;
    max-height: none;
    height: 100%;
    min-height: 0;
    border-bottom: none;
    border-right: 1px solid #e2e8f0;
  }

  .event-page-layout .track-map-wrap {
    flex: 1;
    width: 50%;
    height: 100%;
  }
}
</style>
